import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Popover } from 'module/ui/components/atoms/popover';
import {
  faWarning,
  faLightbulbOn,
  faWrench,
} from '@fortawesome/pro-solid-svg-icons';
import { SignalConfiguration, SignalType } from 'api/signal/types';
import { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';

const TOC_VISIBLE_LINES = 20;

const ToCContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const ToCLine = styled.div<{ isActive: boolean }>`
  width: 12px;
  height: 1px;
  background-color: ${(props) =>
    props.isActive ? 'var(--button-primary)' : 'var(--gray-30)'};
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.isActive ? 1 : 0.5)};
  &:hover {
    opacity: 1;
    background-color: var(--button-primary);
  }
`;

const PopoverContent = styled.div`
  max-height: 50vh;
  overflow-y: auto;
  min-width: 200px;
  max-width: 300px;
  padding: var(--spacing-04);
`;

const PopoverTitle = styled.div`
  font-size: var(--font-size-sm);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--spacing-02);
`;

const PopoverItem = styled.div<{ isActive: boolean }>`
  padding: var(--spacing-02) var(--spacing-03);
  cursor: pointer;
  color: ${(props) =>
    props.isActive ? 'var(--link-color)' : 'var(--text-tertiary)'};
  transition: all 0.2s ease;
  font-size: var(--font-size-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  &:hover {
    background-color: var(--gray-10);
    color: var(--text-primary);
    border-radius: var(--border-radius-xsmall);
  }
`;

const SignalIcon = styled(FontAwesomeIcon)<{ $signalType: SignalType }>`
  color: ${({ $signalType }) => {
    switch ($signalType) {
      case SignalType.Risk:
        return 'var(--red-60)';
      case SignalType.Opportunity:
        return 'var(--green-60)';
      case SignalType.Operational:
        return 'var(--icon-secondary)';
      default:
        return 'var(--text-secondary)';
    }
  }};
`;

interface SignalTOCProps {
  signalConfigs: SignalConfiguration[];
  onSignalSelect?: (signalId: string) => void;
  selectedSignalId?: string;
  title?: string;
}

export const SignalTOC: React.FC<SignalTOCProps> = ({
  signalConfigs,
  onSignalSelect,
  selectedSignalId,
  title,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const getSignalIcon = (signalType: SignalType) => {
    switch (signalType) {
      case SignalType.Risk:
        return faWarning;
      case SignalType.Opportunity:
        return faLightbulbOn;
      case SignalType.Operational:
        return faWrench;
      default:
        return faWarning;
    }
  };

  const signalItems = useMemo(() => {
    return signalConfigs.map((config) => ({
      key: config.signalConfigurationId.toString(),
      label: config.signalName,
      signalType: config.signalType,
    }));
  }, [signalConfigs]);

  const scrollToSignal = useCallback(
    (signalId: string) => {
      if (onSignalSelect) {
        onSignalSelect(signalId);
      }
      setIsPopoverOpen(false);
    },
    [onSignalSelect],
  );

  // Only show first 20 items as lines
  const visibleLineItems = useMemo(
    () => signalItems.slice(0, TOC_VISIBLE_LINES),
    [signalItems],
  );

  const popoverContent = useMemo(
    () => (
      <PopoverContent>
        {title && <PopoverTitle>{title}</PopoverTitle>}
        {signalItems.map((item) => (
          <PopoverItem
            key={item.key}
            isActive={selectedSignalId === item.key}
            onClick={() => scrollToSignal(item.key)}
          >
            <SignalIcon
              icon={getSignalIcon(item.signalType)}
              $signalType={item.signalType}
              size={12}
            />
            {item.label}
          </PopoverItem>
        ))}
      </PopoverContent>
    ),
    [signalItems, selectedSignalId, scrollToSignal, title],
  );

  if (signalItems.length === 0) {
    return null;
  }

  return (
    <Popover
      content={popoverContent}
      trigger="hover"
      placement="right"
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
      overlayStyle={{ zIndex: 1000 }}
      mouseEnterDelay={0}
      mouseLeaveDelay={0.1}
      align={{ offset: [-30, 0] }}
    >
      <div className="table-of-contents">
        <ToCContainer>
          {visibleLineItems.map((item) => (
            <ToCLine
              key={item.key}
              isActive={selectedSignalId === item.key}
              onClick={() => scrollToSignal(item.key)}
              title={item.label}
            />
          ))}
        </ToCContainer>
      </div>
    </Popover>
  );
};
